import type { BoggleRunLedgerEntry } from '~/boggle/engine/standard/types'

import { isAcceptedWordEntry } from './checkTypes'

/** Count accepted words with `atMs` within `windowMs` of run start (0). */
export function countAcceptedInOpeningWindow(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  windowMs: number,
  minLength = 1,
): number {
  let count = 0
  for (let i = 0; i <= index; i++) {
    const candidate = ledger[i]
    if (
      isAcceptedWordEntry(candidate) &&
      candidate.atMs <= windowMs &&
      candidate.word.length >= minLength
    ) {
      count += 1
    }
  }
  return count
}

/**
 * Consecutive accepted words of length ≥ `minLength` (Verbose).
 * Corrections (backspace/clear) do not break the streak; only a shorter accept does.
 */
export function longWordStreakThrough(
  ledger: readonly BoggleRunLedgerEntry[],
  index: number,
  minLength: number,
): number {
  let streak = 0
  for (let i = 0; i <= index; i++) {
    const sliceEntry = ledger[i]
    if (isAcceptedWordEntry(sliceEntry)) {
      if (sliceEntry.word.length >= minLength) {
        streak += 1
      } else {
        streak = 0
      }
    }
  }
  return streak
}

function computeMaxInterWordGapMs(
  ledger: readonly BoggleRunLedgerEntry[],
  options: { excludeOvertime: boolean },
): number {
  let previousAtMs: number | null = null
  let maxGap = 0
  for (const entry of ledger) {
    if (!isAcceptedWordEntry(entry)) {
      continue
    }
    if (options.excludeOvertime && entry.acceptedDuringOvertime) {
      continue
    }
    if (previousAtMs !== null) {
      maxGap = Math.max(maxGap, entry.atMs - previousAtMs)
    }
    previousAtMs = entry.atMs
  }
  return maxGap
}

/** Max gap in ms between consecutive accepted-word ledger entries. */
export function maxInterWordGapMs(ledger: readonly BoggleRunLedgerEntry[]): number {
  return computeMaxInterWordGapMs(ledger, { excludeOvertime: false })
}

/**
 * Max gap between accepted words for Battery badge evaluation.
 * Overtime submissions are skipped entirely — they do not start or end a gap.
 */
export function maxInterWordGapMsForBattery(
  ledger: readonly BoggleRunLedgerEntry[],
): number {
  return computeMaxInterWordGapMs(ledger, { excludeOvertime: true })
}
